/* ============================================================
   teg — services.

   What is meant to be running, whether it is, and the two things
   you actually want when it isn't: the journal, and a restart.
   Only the actions the backend whitelists are offered — a button
   that 403s is worse than no button.
   ============================================================ */

import React, { useState } from 'react';
import { Icon, fmtBytes, fmtUptime } from './lib';
import { api } from './api';

/* action name in the backend whitelist, keyed by service id */
const ACTIONS = {
  'pitvisor-backend': [
    { action: 'restart-pitvisor-backend', label: 'Restart' },
    { action: 'reload-pitvisor-backend', label: 'Reload' },
  ],
  'pitvisor-live': [{ action: 'restart-pitvisor-live', label: 'Restart' }],
  'pitvisor-bot': [{ action: 'restart-pitvisor-bot', label: 'Restart' }],
  'ojee-server': [{ action: 'restart-ojee-server', label: 'Restart' }],
  'mtg-server': [{ action: 'restart-mtg-server', label: 'Restart' }],
  'muw-server': [{ action: 'restart-muw-server', label: 'Restart' }],
  'monopoly-server': [{ action: 'restart-monopoly-server', label: 'Restart' }],
  'dnd-server': [{ action: 'restart-dnd-server', label: 'Restart' }],
  nginx: [{ action: 'restart-nginx', label: 'Restart' }],
  mongodb: [{ action: 'restart-mongodb', label: 'Restart' }],
};

/* File-backed logs. These win over the journal where both exist: nginx's unit
   journal is start/stop chatter, and the error log is the thing anyone opening
   this is looking for. mtg and muw are supervised by pm2, which writes to a
   file instead of journalling at all. */
const LOGS = {
  nginx: 'nginx-error', 'mtg-server': 'mtg-server', 'muw-server': 'muw-server',
};

/* Everything else systemd-supervised journals under its own service id: the
   backend derives that allowlist from the same table it renders here, so a
   service added there gets a Log button without a second list to keep in step.
   mc-api is matched by process but does have a unit behind it. */
const journalOf = (id, v) => (v.type === 'systemd' || id === 'mc-api' ? id : null);

export default function Services({ token, services, toast, refresh }) {
  const [open, setOpen] = useState(null);
  const [logText, setLogText] = useState('');
  const [logBusy, setLogBusy] = useState(false);
  const [running, setRunning] = useState('');

  const rows = services ? Object.entries(services) : [];
  const up = rows.filter(([, v]) => v.active).length;

  const showLogs = async (id, v) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setLogBusy(true); setLogText('');
    try {
      const file = LOGS[id];
      const d = file ? await api.log(token, file)
                     : await api.journal(token, journalOf(id, v), 150);
      setLogText(d.lines || '(nothing logged)');
    } catch (e) {
      setLogText(`Could not read the log: ${e.message}`);
    } finally { setLogBusy(false); }
  };

  const run = async (action, label, id) => {
    setRunning(action);
    try {
      const d = await api.action(token, action);
      if (d.ok) toast('ok', `${label} ${id}: done`);
      else toast('err', `${label} ${id} failed: ${(d.stderr || d.stdout || 'no output').slice(0, 160)}`);
      setTimeout(refresh, 1200);
    } catch (e) {
      toast('err', `${label} ${id} failed: ${e.message}`);
    } finally { setRunning(''); }
  };

  return (
    <div className="tg-view">
      <div className="tg-view-head">
        <h2 className="h2">Services</h2>
        <span className="meta">{up} of {rows.length} running</span>
      </div>

      <div className="panel">
        {rows.map(([id, v]) => {
          const acts = ACTIONS[id] || [];
          const canLog = LOGS[id] || journalOf(id, v);
          return (
            <div key={id} className="tg-svc">
              <div className="tg-svc-row">
                <span className={`dot ${v.active ? 'dot--ok' : 'dot--err'}`}/>
                <span className="tg-svc-name">
                  {v.desc || id}
                  <span className="meta tg-svc-id">{v.type}{v.unit ? ` · ${v.unit}` : ''}{v.pm2_name ? ` · pm2:${v.pm2_name}` : ''}</span>
                </span>
                <span className="tg-svc-facts meta">
                  {v.active ? 'running' : 'stopped'}
                  {v.memory ? ` · ${fmtBytes(v.memory)}` : ''}
                  {v.cpu ? ` · ${v.cpu.toFixed(0)}% cpu` : ''}
                  {v.uptime ? ` · up ${fmtUptime(v.uptime)}` : ''}
                  {v.http_status ? ` · HTTP ${v.http_status}` : ''}
                </span>
                <span className="tg-svc-actions">
                  {canLog && (
                    <button type="button" className={`btn btn--ghost btn--sm ${open === id ? 'tg-on' : ''}`}
                            onClick={() => showLogs(id, v)}>
                      <Icon name="logs" size={13}/> Log
                    </button>
                  )}
                  {acts.map((a) => (
                    <button key={a.action} type="button" className="btn btn--ghost btn--sm"
                            disabled={running === a.action}
                            onClick={() => run(a.action, a.label, v.desc || id)}>
                      <Icon name="restart" size={13}/> {running === a.action ? '…' : a.label}
                    </button>
                  ))}
                </span>
              </div>
              {open === id && (
                <pre className="tg-log">{logBusy ? 'Reading…' : logText}</pre>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="empty tg-empty">No services reported.</div>}
      </div>

      <p className="meta tg-note">
        Restarts run as the dashboard's own user. Anything needing root will say so in the error
        rather than pretending it worked.
      </p>
    </div>
  );
}
